export const TELEMETRY_ENDPOINT = "/telemetry/v1/events";
export const TELEMETRY_SCHEMA_VERSION = "2026-08-30";

export const TELEMETRY_PRODUCT_EVENTS = [
  "forecast_loaded",
  "site_selected",
  "day_changed",
  "hour_inspected",
  "thermal_map_opened",
  "thermal_layer_switched",
  "map_zoom_bucket_reached",
  "static_svg_opened",
  "flight_mode_changed",
  "unit_profile_changed"
] as const;

export const TELEMETRY_FAILURE_EVENTS = [
  "api_failure",
  "load_failure",
  "render_failure"
] as const;

export const TELEMETRY_CONTEXT_FIELDS = [
  "site_slug",
  "day_offset",
  "selected_hour",
  "flight_mode",
  "unit_profile",
  "thermal_layer",
  "zoom_bucket",
  "failure_kind",
  "surface",
  "release_version",
  "release_channel"
] as const;

export const TELEMETRY_EVENT_NAMES = [
  ...TELEMETRY_PRODUCT_EVENTS,
  ...TELEMETRY_FAILURE_EVENTS
] as const;

export const TELEMETRY_TOP_LEVEL_FIELDS = [
  "schema_version",
  "event",
  "context"
] as const;

export const TELEMETRY_FLIGHT_MODES = ["pg", "ppg"] as const;
export const TELEMETRY_UNIT_PROFILES = ["us", "metric"] as const;
export const TELEMETRY_THERMAL_LAYERS = ["density", "episodes"] as const;
export const TELEMETRY_ZOOM_BUCKETS = [
  "overview",
  "region",
  "site",
  "house_thermals"
] as const;
export const TELEMETRY_RELEASE_CHANNELS = [
  "development",
  "preview",
  "production"
] as const;
export const TELEMETRY_FAILURE_KINDS = [
  "aborted",
  "constructor",
  "empty_payload",
  "http_status",
  "invalid_payload",
  "network",
  "runtime",
  "tile",
  "timeout",
  "unsupported_webgl",
  "worker"
] as const;
export const TELEMETRY_SURFACES = [
  "forecast_page",
  "heatmap_page",
  "static_svg",
  "thermal_map"
] as const;
export const TELEMETRY_SITE_SLUGS = siteCatalog.sites.map((site) => site.route_site_slug);

export type TelemetryEventName = (typeof TELEMETRY_EVENT_NAMES)[number];
export type TelemetryContextKey = (typeof TELEMETRY_CONTEXT_FIELDS)[number];
export type TelemetryFlightMode = (typeof TELEMETRY_FLIGHT_MODES)[number];
export type TelemetryUnitProfile = (typeof TELEMETRY_UNIT_PROFILES)[number];
export type TelemetryThermalLayer = (typeof TELEMETRY_THERMAL_LAYERS)[number];
export type TelemetryZoomBucket = (typeof TELEMETRY_ZOOM_BUCKETS)[number];
export type TelemetryReleaseChannel = (typeof TELEMETRY_RELEASE_CHANNELS)[number];
export type TelemetryFailureKind = (typeof TELEMETRY_FAILURE_KINDS)[number];
export type TelemetrySurface = (typeof TELEMETRY_SURFACES)[number];
export type TelemetrySiteSlug = string;

interface TelemetryContextRule {
  required: readonly TelemetryContextKey[];
  optional: readonly TelemetryContextKey[];
}

function eventContextRule(
  required: readonly TelemetryContextKey[],
  optional: readonly TelemetryContextKey[] = []
): TelemetryContextRule {
  return { required, optional };
}

export const TELEMETRY_EVENT_CONTEXT_RULES: Readonly<
  Record<TelemetryEventName, TelemetryContextRule>
> = {
  forecast_loaded: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  site_selected: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  day_changed: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  hour_inspected: eventContextRule([
    "site_slug",
    "day_offset",
    "selected_hour",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  thermal_map_opened: eventContextRule([
    "site_slug",
    "thermal_layer",
    "release_version",
    "release_channel"
  ]),
  thermal_layer_switched: eventContextRule([
    "site_slug",
    "thermal_layer",
    "release_version",
    "release_channel"
  ]),
  map_zoom_bucket_reached: eventContextRule([
    "site_slug",
    "thermal_layer",
    "zoom_bucket",
    "release_version",
    "release_channel"
  ]),
  static_svg_opened: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  flight_mode_changed: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  unit_profile_changed: eventContextRule([
    "site_slug",
    "day_offset",
    "flight_mode",
    "unit_profile",
    "release_version",
    "release_channel"
  ]),
  api_failure: eventContextRule(
    ["surface", "failure_kind", "release_version", "release_channel"],
    ["site_slug", "day_offset", "selected_hour", "flight_mode", "unit_profile"]
  ),
  load_failure: eventContextRule(
    ["surface", "failure_kind", "release_version", "release_channel"],
    ["site_slug", "day_offset", "flight_mode", "unit_profile", "thermal_layer"]
  ),
  render_failure: eventContextRule(
    ["surface", "failure_kind", "release_version", "release_channel"],
    ["site_slug", "thermal_layer", "zoom_bucket"]
  )
};
import siteCatalog from "../../../catalog/generated/sites.json" with { type: "json" };
